import React, { createContext, forwardRef, useContext } from 'react';

import { styled } from '@mui/joy';
import { SxProps } from '@mui/joy/styles/types';
import LockIcon from 'jsx:../assets/icons/lock.svg';

type SegmentedControlSize = 'sm' | 'md';

// Figma <SegmentedControl>/Segment Md 10087:100 and Segment Sm 10158:75122.
const SIZES = {
  md: { height: 40, paddingBlock: 4, paddingInline: 16, gap: 8, icon: 24 },
  sm: { height: 32, paddingBlock: 2, paddingInline: 12, gap: 6, icon: 20 },
} as const;

const SEGMENT_CLASS = 'SegmentedControl-segment';
const LOCK_CLASS = 'SegmentedControl-lock';

const SizeContext = createContext<SegmentedControlSize>('md');

// Figma paints the frame's 1px stroke over the segments, so each segment draws its share
// of it. A segment may sit inside a Tooltip's <span>, hence the second selector of each pair.
const Root = styled('div')(({ theme }) => {
  const stroke = theme.vars.palette.border.border;
  const radius = theme.vars.radius.md;
  return {
    display: 'flex',
    [`& > .${SEGMENT_CLASS}:first-child, & > :first-child > .${SEGMENT_CLASS}`]: {
      borderLeftColor: stroke,
      borderTopLeftRadius: radius,
      borderBottomLeftRadius: radius,
    },
    [`& > .${SEGMENT_CLASS}:last-child, & > :last-child > .${SEGMENT_CLASS}`]: {
      borderRight: `1px solid ${stroke}`,
      paddingRight: 'calc(var(--Segment-paddingInline) - 1px)',
      borderTopRightRadius: radius,
      borderBottomRightRadius: radius,
    },
  };
});

const SegmentRoot = styled('button', {
  shouldForwardProp: (prop) => prop !== 'selected' && prop !== 'size',
})<{ selected: boolean; size: SegmentedControlSize }>(({ theme, selected, size }) => {
  const tokens = theme.vars.palette.segmentedControl;
  const stroke = theme.vars.palette.border.border;
  const white = theme.vars.palette.common.white;
  const { height, paddingBlock, paddingInline, gap } = SIZES[size];
  return {
    // Each segment owns the divider on its left edge (Figma "Divider"); the borders take
    // 1px, so the padding gives it back to keep the Figma widths.
    '--Segment-paddingInline': `${paddingInline}px`,
    '--Segment-divider': selected ? tokens.selectedBg : stroke,
    '--Segment-lock': selected ? white : tokens.lock,
    boxSizing: 'border-box',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    verticalAlign: 'top',
    gap,
    height,
    margin: 0,
    paddingBlock: paddingBlock - 1,
    paddingLeft: 'calc(var(--Segment-paddingInline) - 1px)',
    paddingRight: 'var(--Segment-paddingInline)',
    border: 0,
    borderTop: `1px solid ${stroke}`,
    borderBottom: `1px solid ${stroke}`,
    borderLeft: '1px solid var(--Segment-divider)',
    borderRadius: 0,
    backgroundColor: selected ? tokens.selectedBg : tokens.bg,
    color: selected ? white : theme.vars.palette.text.primary,
    fontFamily: theme.vars.fontFamily.body,
    fontSize: 14,
    fontWeight: 400,
    lineHeight: '14px',
    whiteSpace: 'nowrap',
    cursor: 'pointer',
    userSelect: 'none',
    [`& .${LOCK_CLASS}`]: { flexShrink: 0, color: 'var(--Segment-lock)' },
    '&:hover, &:focus-visible': {
      outline: 'none',
      backgroundColor: selected ? tokens.selectedHoverBg : tokens.hoverBg,
      ...(selected && { '--Segment-divider': tokens.selectedHoverBg }),
    },
    ...(selected && {
      '&:focus-visible': {
        boxShadow: `inset 0 0 0 3px ${theme.vars.palette.primary.softActiveColor}`,
      },
    }),
    '&:disabled': {
      cursor: 'not-allowed',
      backgroundColor: tokens.disabledBg,
      color: tokens.disabledColor,
      '--Segment-lock': tokens.disabledColor,
      '--Segment-divider': selected ? tokens.disabledSelectedDivider : stroke,
    },
  };
});

interface SegmentedControlProps extends React.HTMLAttributes<HTMLDivElement> {
  size?: SegmentedControlSize;
  sx?: SxProps;
}

/** Figma <SegmentedControl> 10157:75332: Md is 40px high, Sm 32px. */
export const SegmentedControl = ({ size = 'md', children, ...rest }: SegmentedControlProps) => (
  <SizeContext.Provider value={size}>
    <Root role='group' {...rest}>
      {children}
    </Root>
  </SizeContext.Provider>
);

interface SegmentProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  selected?: boolean;
  /** Adds the lock icon; the segment stays clickable so it can lead to an upgrade. */
  locked?: boolean;
  endDecorator?: React.ReactNode;
}

export const Segment = forwardRef<HTMLButtonElement, SegmentProps>(function Segment(
  { selected = false, locked = false, endDecorator, className, children, type = 'button', ...rest },
  ref
) {
  const size = useContext(SizeContext);
  const { icon } = SIZES[size];
  return (
    <SegmentRoot
      ref={ref}
      type={type}
      selected={selected}
      size={size}
      aria-pressed={selected}
      className={className ? `${SEGMENT_CLASS} ${className}` : SEGMENT_CLASS}
      {...rest}
    >
      {children}
      {endDecorator}
      {locked && <LockIcon className={LOCK_CLASS} width={icon} height={icon} aria-hidden />}
    </SegmentRoot>
  );
});
